import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { parseDateInUtc, addToDate } from '../dates/date.utils';

export interface MedicareValidationResult {
    isValid: boolean;
    errorMessage: string | null;
}

export type DateCategory = 'past-or-today' | 'today' | 'future1month' | 'future2-3months' | null;

/**
 * Validates Medicare start date according to business rules
 * @param {string|Date} date - The Medicare start date to validate
 * @param {string|Date} dateOfBirth - Member's date of birth
 * @param {string|Date} serverDate - Current server date
 * @returns {MedicareValidationResult} Validation result with error message if invalid
 */
export const validateMedicareStartDate = (
    date: string | Date | number | null,
    dateOfBirth: string | Date | number,
    serverDate: string | Date | number
): MedicareValidationResult => {

    // Rule 1: Invalid date format or empty
    if (!date) {
        return {
            isValid: false,
            errorMessage: VALIDATION_ERROR_MESSAGES.DATE
        };
    }

    const medicareDate = parseDateInUtc(date);
    const dob = parseDateInUtc(dateOfBirth);
    const today = parseDateInUtc(serverDate);

    // Check if date is valid
    if (!medicareDate.isValid()) {
        return {
            isValid: false,
            errorMessage: VALIDATION_ERROR_MESSAGES.DATE
        };
    }

    // Rule 2: Must be after date of birth
    if (medicareDate.isSameOrBefore(dob, 'day')) {
        return {
            isValid: false,
            errorMessage: VALIDATION_ERROR_MESSAGES.MEDICARE_AFTER_DOB
        };
    }

    // Rule 3: Cannot be more than 3 months in the future
    const threeMonthsFromNow = addToDate(3, 'months', today.startOf('day'));

    if (medicareDate.isAfter(threeMonthsFromNow, 'day')) {
        return {
            isValid: false,
            errorMessage: VALIDATION_ERROR_MESSAGES.MEDICARE_FUTURE_LIMIT
        };
    }

    // Rule 4: Must be 1st of the month
    if (medicareDate.date() !== 1) {
        return {
            isValid: false,
            errorMessage: VALIDATION_ERROR_MESSAGES.MEDICARE_FIRST_OF_MONTH
        };
    }

    return { isValid: true, errorMessage: null, };
};

/**
 * Determine if Medicare start date is in the second or third future month
 * @param {string|Date} medicareStartDate - Medicare start date
 * @param {string|Date} serverDate - Current server date
 * @returns {Boolean}
 */
export const checkSecondOrThirdFutureMonth = (
    medicareStartDate: string | Date | number,
    serverDate: string | Date | number
): boolean => {
    const medicareDate = parseDateInUtc(medicareStartDate);

    // Normalize to first of month for comparison - use dayjs() to create new instances
    const firstOf2ndMonth = parseDateInUtc(serverDate).add(2, 'month').startOf('month');
    const firstOf3rdMonth = parseDateInUtc(serverDate).add(3, 'month').startOf('month');

    return medicareDate.isSame(firstOf2ndMonth, 'day') || medicareDate.isSame(firstOf3rdMonth, 'day');
};

/**
 * Detects whether the given address line contains a PO Box or Private Mailbox
 * (PMB) pattern. Matching is case-insensitive and ignores punctuation
 * (`.`, `,`, `#`) and spacing differences so that variations like
 * "PO Box", "P.O. Box", "P O Box", "POBOX", "PO BX", "Post Office Box",
 * "Post Box", "POB", "P.O.B.", "PMB", "P.M.B.", "Private Mailbox" and
 * "Private Mail Box" are all detected.
 * Blocks Patterns as per HIX-227975 dated Jun 10, 2026 and HIX-230163 dated Jul 6
 *
 * The caller is responsible for choosing what to compare - for the GA
 * Home Address requirement we evaluate each line and the combined
 * "line 1 + line 2" string so that patterns split across the two
 * lines (for example "PO" on line 1 and "Box 123" on line 2) are
 * still caught.
 */
const PO_BOX_PATTERNS: RegExp[] = [
    /\bp\s*o\s+box(?:es)?\b/,
    /\bpost\s+office\s+box\b/,
    /\bpobox\b/,
    /\bp\s*o\s+bx\b/,
    /\bp\s*o\s*b\b/,
    /\bp\s*m\s*b\b/,
    /\bprivate\s+mail\s*box\b/,
    /\bpost\s+box\b/,
];

const normalizeForPoBoxCheck = (value: string): string =>
    value
        .toLowerCase()
        .replace(/[.,#]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export const isPoBoxOrPmb = (value: string | null | undefined): boolean => {
    if (value === null || value === undefined) {
        return false;
    }

    const stringValue = typeof value === 'string' ? value : String(value);

    if (stringValue.trim() === '') {
        return false;
    }

    const normalized = normalizeForPoBoxCheck(stringValue);

    return PO_BOX_PATTERNS.some(pattern => pattern.test(normalized));
};

export interface AddressLinePoBoxFlags {
    line1HasPoBoxError: boolean;
    line2HasPoBoxError: boolean;
}

/**
 * Decides, for a Home Address split across two lines, which line(s) should be flagged as a PO Box / PMB error. 
 *
 * - flag a line when the line itself matches a PO Box / PMB pattern;
 * - flag both lines when neither matches individually but the combined
 *   "line 1 + line 2" string does (split-across pattern like "PO" / "Box 123");
 */
export const getAddressLinePoBoxFlags = (
    streetAddress1: string | null | undefined,
    streetAddress2: string | null | undefined
): AddressLinePoBoxFlags => {
    const line1HasPoBox = isPoBoxOrPmb(streetAddress1);
    const line2HasPoBox = isPoBoxOrPmb(streetAddress2);
    const combinedHasPoBox = isPoBoxOrPmb(`${streetAddress1 || ''} ${streetAddress2 || ''}`);
    const isPoBoxSplitAcrossLines = combinedHasPoBox && !line1HasPoBox && !line2HasPoBox;

    return {
        line1HasPoBoxError: line1HasPoBox || isPoBoxSplitAcrossLines,
        line2HasPoBoxError: line2HasPoBox || isPoBoxSplitAcrossLines,
    };
};
